import { AccountAutoComplete } from '@/components/common/account-auto-complete';
import { FreshShipmentsDropdown } from '@/components/common/fresh-shipments-dropdown/FreshShipmentsDropdown';
import { SiteSelect } from '@/components/common/site-select';
import { SitesAutoComplete } from '@/components/common/sites-auto-complete';
import { YardsAutoComplete } from '@/components/common/yards-auto-complete';
import { DatePicker } from '@/components/ui/date-picker';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';

/**
 * Filter surface bound to the URL-synced session hook.
 *
 * Fert site and fresh shipment use their existing shared dropdowns.
 * Transfer site uses SitesAutoComplete (supports defaultValueId reverse-lookup).
 * Account uses AccountAutoComplete (supports defaultId reverse-lookup).
 * Farm location uses YardsAutoComplete (defaultValueId added in this pass).
 * Load date stays as a plain text input (AM/PM label, not a date).
 *
 * Responsive grid: stacks 1 col on mobile, 2 on sm, 4 on lg.
 */

export interface FreshStrawFilterPanelProps {
  session: FreshStrawSession;
  className?: string;
}

function toDate(value: string): Date | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function fromDate(date: Date | undefined): string {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function FreshStrawFilterPanel({
  session,
  className,
}: FreshStrawFilterPanelProps) {
  return (
    <section
      className={cn(
        'ring-border bg-background rounded-lg p-3 ring-1',
        className,
      )}
      data-slot="fresh-straw-filter-panel"
      aria-label="Fresh straw filters"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field>
          <FieldLabel htmlFor="fs-fert-date">Fert date</FieldLabel>
          <DatePicker
            value={toDate(session.fertDate)}
            onChange={(date) => session.setFertDate(fromDate(date))}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fs-fert-site">Fert site</FieldLabel>
          <SiteSelect
            urlSync={false}
            filter="fertilization"
            value={session.fertSite || undefined}
            onChange={(id) => session.setFertSite(id ?? '')}
            placeholder="Select fert site"
            triggerClassName="w-full"
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fs-transfer-date">Transfer date</FieldLabel>
          <DatePicker
            value={toDate(session.transferDate)}
            onChange={(date) => session.setTransferDate(fromDate(date))}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fs-transfer-site">Transfer site</FieldLabel>
          <SitesAutoComplete
            defaultValueId={session.transferSite || undefined}
            onSelect={(e) => session.setTransferSite(e.value?.id ?? '')}
            onChange={(e) => {
              if (typeof e.value === 'string' && !e.value)
                session.setTransferSite('');
            }}
            placeholder="Search transfer site"
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fs-account">Account</FieldLabel>
          <AccountAutoComplete
            defaultId={session.accountId || undefined}
            onSelect={(e) => session.setAccountId(e.value?.id ?? '')}
            onChange={(e) => {
              if (typeof e.value === 'string' && !e.value)
                session.setAccountId('');
            }}
            placeholder="Search account"
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fs-location">Farm location</FieldLabel>
          <YardsAutoComplete
            defaultValueId={session.locationId || undefined}
            onSelect={(e) => session.setLocationId(e.value?.id ?? '')}
            onChange={(e) => {
              if (typeof e.value === 'string' && !e.value)
                session.setLocationId('');
            }}
            placeholder="Search farm location"
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fs-fresh-shipment">Fresh shipment</FieldLabel>
          <FreshShipmentsDropdown
            inputId="fs-fresh-shipment"
            fertDate={session.fertDate}
            fertSiteId={session.fertSite}
            value={session.freshShipmentId || undefined}
            onChange={(e) => session.setFreshShipmentId(e.value ?? '')}
            placeholder="Select fresh shipment"
            disabled={!session.fertDate || !session.fertSite}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fs-load-date">Load date</FieldLabel>
          <Input
            id="fs-load-date"
            value={session.loadDate}
            onChange={(e) => session.setLoadDate(e.target.value)}
            placeholder="AM / PM"
          />
        </Field>
      </div>
    </section>
  );
}
